import {Badge} from '@/components/ui/badge/Badge'
import {
    StyledAgendaEventProjectInfo,
    StyledExpirationDateInfo
} from '@/features/agenda/components/agenda-event-project-info/style'
import dayjs, {formatLocaleDate} from '@/dayjs'
import {Trans, useTranslation} from '@/translations/i18n'
import {projectStatusToBadgeVariant, projectStatusToLabel} from '@/features/projects/utils'
import {ProjectSmall} from '@/features/projects/types'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {Text} from '@/components/ui/text/Text'
import {isValidDate} from '@/components/commons/input-date/utils'
import {generatePath} from 'react-router-dom'
import {routes} from '@/utilities/constants/routes'
import {usePermissions} from '@/features/auth/store/store'

interface AgendaEventProjectInfoProps {
    patientName: string
    project?: Pick<ProjectSmall, 'id' | 'code' | 'startDate' | 'expirationDate' | 'status' | 'isSuspended'> | null
    showProjectLabel?: boolean
    disableNavigation?: boolean
}

export const AgendaEventProjectInfo = ({
    patientName,
    project,
    showProjectLabel,
    disableNavigation
}: AgendaEventProjectInfoProps) => {
    const {t} = useTranslation()
    const permissions = usePermissions()

    if (!project) {
        return (
            <StyledAgendaEventProjectInfo align="end">
                <Text palette="neutral" shade="500" size="textSm">
                    {t('agenda:no_project_found', {name: patientName})}
                </Text>
            </StyledAgendaEventProjectInfo>
        )
    }

    return (
        <StyledAgendaEventProjectInfo gap={1.5} fullWidth align="end">
            {permissions?.projects?.read && !disableNavigation ? (
                <ButtonLink
                    to={generatePath(routes.PROJECT_DETAILS_ROUTE.handle.path, {id: project.id})}
                    variant="tertiary"
                    shape="text"
                >
                    {showProjectLabel ? (
                        <Trans i18nKey="agenda:pri_code" values={{code: project.code}} components={{b: <b />}} />
                    ) : (
                        <b>{project.code}</b>
                    )}
                </ButtonLink>
            ) : (
                <Text size="textSm" weight="500">
                    {showProjectLabel ? (
                        <Trans i18nKey="agenda:pri_code" values={{code: project.code}} components={{b: <b />}} />
                    ) : (
                        <b>{project.code}</b>
                    )}
                </Text>
            )}
            {project.status && !project.isSuspended && (
                <Badge size="sm" shape="rectangle" variant={projectStatusToBadgeVariant[project.status]}>
                    {t(projectStatusToLabel[project.status])}
                </Badge>
            )}

            {project.isSuspended && (
                <Badge size="sm" shape="rectangle" variant="danger">
                    {t('commons:suspended')}
                </Badge>
            )}

            {project.expirationDate && isValidDate(dayjs(project.expirationDate).format('YYYY-MM-DD'), 'YYYY-MM-DD') ? (
                <StyledExpirationDateInfo>
                    {t('agenda:project_expires_on', {
                        date: formatLocaleDate(dayjs(project.expirationDate).toDate(), 'll')
                    })}
                </StyledExpirationDateInfo>
            ) : (
                <Text palette="neutral" shade="500" size="textSm">
                    {t('agenda:project_expiration_date_not_defined')}
                </Text>
            )}
        </StyledAgendaEventProjectInfo>
    )
}
